import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ClipboardCheck,
  Languages,
  Send,
  Sparkles,
} from "lucide-react";
import type { Language, SurveySubmission } from "../types";
import {
  copy,
  defaultSubmission,
  getOptionLabel,
  getQuestion,
  questions,
  steps,
} from "../data/survey";
import { ProgressMeter } from "../components/ProgressMeter";
import { getMukimForKampung } from "../data/locations";
import { formatServiceError } from "../lib/errors";
import { getSessionKey, resetSessionKey } from "../lib/storage";
import { submitSurvey, trackEvent } from "../lib/supabase";

type Props = {
  language: Language;
  setLanguage: (language: Language) => void;
};

type Errors = Partial<Record<keyof SurveySubmission, string>>;

const pageMotion = {
  initial: { opacity: 0, y: 18, scale: 0.985 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -18, scale: 0.985 },
  transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const },
};

function validateFields(
  submission: SurveySubmission,
  fields: Array<keyof SurveySubmission>,
  language: Language,
) {
  const errors: Errors = {};
  fields.forEach((field) => {
    const question = getQuestion(field);
    const value = submission[field].trim();
    if (question.required && !value) {
      errors[field] = copy.required[language];
    }
  });
  return errors;
}

function WordCount({ value, language }: { value: string; language: Language }) {
  const count = value.trim() ? value.trim().split(/\s+/).length : 0;
  return (
    <span className="word-count">
      {count} {copy.words[language]}
    </span>
  );
}

export default function SurveyPage({ language, setLanguage }: Props) {
  const [submission, setSubmission] =
    useState<SurveySubmission>(defaultSubmission);
  const [stepIndex, setStepIndex] = useState(0);
  const [errors, setErrors] = useState<Errors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const sessionKey = useMemo(() => getSessionKey(), []);
  const startedRef = useRef(false);
  const activeStep = steps[stepIndex];
  const progress = Math.round((stepIndex / (steps.length - 1)) * 100);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void trackEvent({
      session_key: sessionKey,
      event_type: "started",
      step_index: 0,
      language,
    });
  }, [language, sessionKey]);

  useEffect(() => {
    void trackEvent({
      session_key: sessionKey,
      event_type: "step_viewed",
      step_index: stepIndex,
      language,
    });
  }, [language, sessionKey, stepIndex]);

  const updateField = (field: keyof SurveySubmission, value: string) => {
    if (field === "nama_kampung") {
      const mukim = getMukimForKampung(value);
      setSubmission((current) => ({
        ...current,
        nama_kampung: value,
        mukim,
      }));
      setErrors((current) => ({
        ...current,
        nama_kampung: undefined,
        mukim: undefined,
      }));
      return;
    }

    setSubmission((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const goNext = () => {
    const stepErrors = validateFields(
      submission,
      activeStep.questionIds,
      language,
    );
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length) return;
    setStepIndex((current) => Math.min(current + 1, steps.length - 1));
  };

  const goBack = () => {
    setStepIndex((current) => Math.max(current - 1, 0));
  };

  const handleSubmit = async () => {
    const allFields = questions.map((question) => question.id);
    const allErrors = validateFields(submission, allFields, language);
    setErrors(allErrors);
    if (Object.keys(allErrors).length) {
      const firstError = questions.find((question) => allErrors[question.id]);
      const targetStep = steps.findIndex((step) =>
        step.questionIds.includes(firstError!.id),
      );
      setStepIndex(Math.max(targetStep, 1));
      return;
    }

    const resolvedSubmission =
      submission.mukim || submission.nama_kampung
        ? {
            ...submission,
            mukim:
              submission.mukim ||
              getMukimForKampung(submission.nama_kampung),
          }
        : submission;

    setIsSubmitting(true);
    setSubmitError("");
    try {
      await submitSurvey(resolvedSubmission, language);
      await trackEvent({
        session_key: sessionKey,
        event_type: "completed",
        step_index: steps.length - 1,
        language,
      });
      resetSessionKey();
      setIsComplete(true);
    } catch (error) {
      setSubmitError(
        formatServiceError(
          error,
          language === "ms"
            ? "Borang tidak dapat dihantar. Sila cuba lagi."
            : "Unable to submit survey. Please try again.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetSurvey = () => {
    setSubmission(defaultSubmission);
    setStepIndex(0);
    setErrors({});
    setIsComplete(false);
    setSubmitError("");
  };

  if (isComplete) {
    return (
      <main className="survey-page">
        <section className="success-panel">
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 240, damping: 18 }}
            className="success-badge"
          >
            <Check size={38} />
          </motion.div>
          <h1>{copy.thankYou[language]}</h1>
          <p>{copy.thankYouDetail[language]}</p>
          <button className="primary-button" onClick={resetSurvey}>
            <ClipboardCheck size={18} />
            {copy.another[language]}
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="survey-page">
      <section className="survey-hero">
        <div className="hero-copy">
          <span className="eyebrow">Daerah Putatan</span>
          <h1>{copy.appName[language]}</h1>
          <p>{copy.subtitle[language]}</p>
        </div>
        <div className="district-visual" aria-hidden="true">
          <span className="route route-a" />
          <span className="route route-b" />
          <span className="route route-c" />
          <span className="marker marker-a" />
          <span className="marker marker-b" />
          <span className="marker marker-c" />
        </div>
      </section>

      <section className="survey-frame" aria-live="polite">
        <ProgressMeter
          className="survey-progress"
          label={activeStep.eyebrow[language]}
          current={stepIndex + 1}
          total={steps.length}
          value={progress}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep.id}
            {...pageMotion}
            className={`survey-card survey-card-${activeStep.id}`}
          >
            {activeStep.id === "welcome" ? (
              <WelcomeStep language={language} setLanguage={setLanguage} />
            ) : activeStep.id === "review" ? (
              <ReviewStep submission={submission} language={language} />
            ) : (
              <>
                <div className="step-heading">
                  <span>{activeStep.eyebrow[language]}</span>
                  <h2>{activeStep.title[language]}</h2>
                  <p>{activeStep.description[language]}</p>
                </div>

                <div className="question-stack">
                  {activeStep.questionIds.map((questionId) => {
                    const question = getQuestion(questionId);
                    return (
                      <div className="question-block" key={question.id}>
                        <label className="question-label">
                          {question.label[language]}
                        </label>
                        {question.helper ? (
                          <p className="question-helper">
                            {question.helper[language]}
                          </p>
                        ) : null}

                        {question.type === "single" ? (
                          <div
                            className="option-grid"
                            role="group"
                            aria-label={question.label[language]}
                          >
                            {question.options?.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className={`option-card ${
                                  submission[question.id] === option.value
                                    ? "is-selected"
                                    : ""
                                }`}
                                aria-pressed={submission[question.id] === option.value}
                                onClick={() =>
                                  updateField(question.id, option.value)
                                }
                              >
                                <span className="option-dot" />
                                <span className="option-text">
                                  <span className="option-title">
                                    {option.label[language]}
                                  </span>
                                  {option.description?.[language] ? (
                                    <span className="option-description">
                                      {option.description[language]}
                                    </span>
                                  ) : null}
                                </span>
                                </button>
                            ))}
                          </div>
                        ) : question.type === "select" ? (
                          <div className="select-wrap">
                            <select
                              className="select-field"
                              value={submission[question.id]}
                              aria-label={question.label[language]}
                              aria-invalid={Boolean(errors[question.id])}
                              onChange={(event) =>
                                updateField(question.id, event.target.value)
                              }
                            >
                              <option value="" disabled>
                                {language === "ms"
                                  ? "Sila pilih"
                                  : "Please select"}
                              </option>
                              {question.options?.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label[language]}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="textarea-wrap">
                            <textarea
                              value={submission[question.id]}
                              onChange={(event) =>
                                updateField(question.id, event.target.value)
                              }
                              placeholder={question.placeholder?.[language]}
                              aria-label={question.label[language]}
                              aria-invalid={Boolean(errors[question.id])}
                              rows={5}
                            />
                            <WordCount
                              value={submission[question.id]}
                              language={language}
                            />
                          </div>
                        )}

                        {errors[question.id] ? (
                          <p className="field-error">{errors[question.id]}</p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {submitError ? <div className="submit-error">{submitError}</div> : null}

        <div className="survey-actions">
          <button
            className="secondary-button"
            onClick={goBack}
            disabled={stepIndex === 0 || isSubmitting}
          >
            <ArrowLeft size={18} />
            {copy.back[language]}
          </button>

          {stepIndex === steps.length - 1 ? (
            <button
              className="primary-button"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? copy.submitting[language] : copy.submit[language]}
              <Send size={18} />
            </button>
          ) : (
            <button className="primary-button" onClick={goNext}>
              {stepIndex === 0 ? copy.start[language] : copy.next[language]}
              <ArrowRight size={18} />
            </button>
          )}
        </div>
      </section>
    </main>
  );
}

function WelcomeStep({
  language,
  setLanguage,
}: {
  language: Language;
  setLanguage: (language: Language) => void;
}) {
  return (
    <div className="welcome-step">
      <span className="spark-chip">
        <Sparkles size={16} />
        {language === "ms" ? "2 minit" : "2 minutes"}
      </span>
      <h2>{steps[0].title[language]}</h2>
      <p>{steps[0].description[language]}</p>

      <div className="language-panel">
        <Languages size={19} />
        <div>
          <strong>{language === "ms" ? "Bahasa" : "Language"}</strong>
          <span>
            {language === "ms"
              ? "Tukar bahasa bila-bila masa."
              : "Switch language anytime."}
          </span>
        </div>
        <div className="segmented">
          <button
            className={language === "ms" ? "is-active" : ""}
            onClick={() => setLanguage("ms")}
            type="button"
          >
            BM
          </button>
          <button
            className={language === "en" ? "is-active" : ""}
            onClick={() => setLanguage("en")}
            type="button"
          >
            EN
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewStep({
  submission,
  language,
}: {
  submission: SurveySubmission;
  language: Language;
}) {
  return (
    <div className="review-step">
      <div className="step-heading">
        <span>{steps[4].eyebrow[language]}</span>
        <h2>{steps[4].title[language]}</h2>
        <p>{copy.review[language]}</p>
      </div>

      <div className="review-grid">
        {questions.map((question) => (
          <div className="review-item" key={question.id}>
            <span>{question.label[language]}</span>
            <strong>
              {question.type === "single" || question.type === "select"
                ? getOptionLabel(question.id, submission[question.id], language)
                : submission[question.id] || "-"}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
}
